import {
  CommunicationsDataset,
  CommunicationsFilteringMode,
  DatasetCommunication,
  SiteCommunicationsDataset,
} from '../types';
import { isInTimeWindow } from './time-window.helper';

export type ParsedCommunicationsDataset = {
  communications: DatasetCommunication[];
  subscribeTo?: string[];
  objectId?: string;
};

export const parseCommunicationsDataset = (
  dataset: SiteCommunicationsDataset | CommunicationsDataset,
) => {
  const parsedDataset: ParsedCommunicationsDataset = {
    communications: JSON.parse(dataset.communications ?? '[]') || [],
  };
  if ('subscribe_to' in dataset && dataset.subscribe_to)
    parsedDataset.subscribeTo = JSON.parse(dataset.subscribe_to) || [];
  if ('object_id' in dataset && dataset.object_id) parsedDataset.objectId = dataset.object_id;
  return parsedDataset;
};

export const communicationsDatasetAlias = (datasetName: string) => {
  return `communications_alias_${datasetName}`;
};

export const filterSiteDisabledCommunications = (
  communications: DatasetCommunication[],
  objectId: string,
) => {
  return communications.filter((communication) => {
    return !communication.disabledSites?.includes(objectId);
  });
};

export const filterCommunicationsByZones = (
  communications: DatasetCommunication[],
  zones: string[],
  mode: CommunicationsFilteringMode,
) => {
  if (mode === CommunicationsFilteringMode.MY_ZONES_ONLY)
    return communications.filter((communication) => {
      return communication.zones.some((zone) => zones.includes(zone));
    });
  if (mode === CommunicationsFilteringMode.WITHOUT_ZONES_ONLY)
    return communications.filter((communication) => {
      return (communication.zones ?? []).length === 0;
    });
  // all
  return communications;
};

export const filterActiveCommunications = (communications: DatasetCommunication[]) => {
  return communications.filter((communication) => {
    try {
      const now = new Date().getTime();
      const startAt = new Date(communication.startAt).getTime();
      const endAt = new Date(communication.endAt).getTime();
      const hasAtLeastOneFilename =
        Boolean(communication.horizontalFilename) || Boolean(communication.verticalFilename);
      return (
        now >= startAt &&
        now <= endAt &&
        hasAtLeastOneFilename &&
        isInTimeWindow(communication.timeWindow)
      );
    } catch (error) {
      return false;
    }
  });
};

export const getCommunicationType = (communication: DatasetCommunication): 'image' | 'video' => {
  const filenameForComparison = communication.horizontalFilename || communication.verticalFilename;
  if (filenameForComparison) {
    const extension = filenameForComparison.split('.').pop();
    return extension === 'mp4' ? 'video' : 'image';
  }
  return 'image';
};

export const getActiveCommunicationsFromDataset = ({
  dataset,
  zones,
  mode,
}: {
  dataset: CommunicationsDataset;
  zones: string[];
  mode: CommunicationsFilteringMode;
}) => {
  const parsedCommunications = parseCommunicationsDataset(dataset);
  let { communications } = parsedCommunications;
  communications = filterActiveCommunications(communications);
  communications = filterCommunicationsByZones(communications, zones, mode);
  const siteIdToFilterOn = parsedCommunications.objectId;
  if (siteIdToFilterOn)
    communications = filterSiteDisabledCommunications(communications, siteIdToFilterOn);
  return communications;
};

export const sortCommunicationsByName = (communications: DatasetCommunication[]) => {
  return communications.sort((a, b) => {
    if (a.orderingName && b.orderingName)
      return a.orderingName.localeCompare(b.orderingName, 'fr', { numeric: true });
    return 0;
  });
};
